// src/lib/posthog.ts
import posthog from 'posthog-js';
import type { PostHogConfig } from 'posthog-js';
import { API_CONFIG, TELEMETRY_DISABLED } from './config';
import logger from './logger';

const POSTHOG_PROJECT_TOKEN = import.meta.env.VITE_POSTHOG_PROJECT_TOKEN || '';
const POSTHOG_HOST = import.meta.env.VITE_POSTHOG_HOST || 'https://us.i.posthog.com';
const IS_DEV = import.meta.env.DEV;

let initialized = false;

function getEnvironment(): string {
  if (IS_DEV) return 'development';
  if (API_CONFIG.baseURL.includes('staging')) return 'staging';
  return 'production';
}

function getPostHogOptions(): Partial<PostHogConfig> {
  const environment = getEnvironment();

  return {
    api_host: POSTHOG_HOST,
    defaults: '2026-01-30',
    debug: IS_DEV,
    capture_pageview: false,
    capture_pageleave: true,
    respect_dnt: true,
    person_profiles: 'identified_only',
    loaded: (client) => {
      client.register({
        app_environment: environment,
        app_name: 'Insider Performance',
      });
    },
  };
}

export function initPostHog(): boolean {
  if (TELEMETRY_DISABLED) {
    logger.warn('PostHog: telemetry disabled for this environment');
    return false;
  }

  if (!POSTHOG_PROJECT_TOKEN) {
    logger.warn('PostHog: No project token configured - analytics disabled');
    return false;
  }

  if (initialized) {
    return true;
  }

  try {
    posthog.init(POSTHOG_PROJECT_TOKEN, getPostHogOptions());
    initialized = true;
    logger.log(`PostHog initialized for ${getEnvironment()} environment`);
    return true;
  } catch (error) {
    console.error('PostHog: Failed to initialize', error);
    return false;
  }
}

export function isPostHogEnabled(): boolean {
  return initialized && !TELEMETRY_DISABLED;
}

export { posthog };
